import { Component, inject, signal, WritableSignal } from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CsIconModule } from '@components/icon';
import { BroadcastService } from '@services/broadcast-service';
import { EventLoggerService } from '@services/event-logger-service';
import { OnboardingLocationService } from '@services/onboarding/onboarding-location.service';
import { LocationExampleCardComponent } from './location-example-card.component';
import { LocationExample, LocationExampleConcern, resolveLocationExamples } from './location-examples.constants';

/** Dismiss payload meaning the user answered — granted a location, or explicitly declined via Skip. */
export const LOCATION_ANSWERED = 'ANSWERED';

@Component({
  selector: 'app-onboarding-location-sheet',
  templateUrl: './onboarding-location-sheet.component.html',
  imports: [ButtonCtaComponent, CsIconModule, LocationExampleCardComponent],
})
export class OnboardingLocationSheetComponent {
  private readonly bottomSheetRef: MatBottomSheetRef = inject(MatBottomSheetRef);
  private readonly locationService: OnboardingLocationService = inject(OnboardingLocationService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  /** The concern the user just picked; it is not saved yet, so the opener has to pass it in. */
  private readonly concern: LocationExampleConcern | null = inject(MAT_BOTTOM_SHEET_DATA, { optional: true });

  protected readonly loading: WritableSignal<boolean> = signal(false);

  /** The two "for eg." lines, chosen from the user's primary concern and its class. */
  protected readonly examples: LocationExample[] = resolveLocationExamples(this.concern ?? { concernClass: '' });

  constructor() {
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'onboarding-location-sheet' }));
  }

  protected async useMyLocation(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    const granted = await this.locationService.captureLocation();
    this.loading.set(false);
    if (granted) this.bottomSheetRef.dismiss(LOCATION_ANSWERED);
  }

  /** Records the decline so onboarding never asks again; dismisses even if that write fails. */
  protected async skip(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    try {
      await this.locationService.skipLocation();
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: String(error) });
    } finally {
      this.loading.set(false);
    }
    this.bottomSheetRef.dismiss(LOCATION_ANSWERED);
  }

  /** Records nothing; the empty payload keeps the caller on the concern screen. */
  protected close(): void {
    if (this.loading()) return;
    this.bottomSheetRef.dismiss();
  }

  async handleBackPress(): Promise<boolean> {
    this.close();
    return true;
  }
}
